import {
  useCallback,
  useState,
  useRef,
  useEffect,
  type Dispatch,
  type SetStateAction,
  type RefObject,
} from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  Combobox,
  ComboboxInput,
  ComboboxList,
  ComboboxOption,
  Heading,
  Icon,
} from '@acast-tech/decibel';
import { ChevronDownRegularGlyph } from '@acast-tech/decibel-glyphs';
import useClickOutside from '@/hooks/useClickOutside';
import { useFilterContext } from '@/context';
type Item = { label: string; value: string };

export type PageHeadingProps = {
  items: Item[];
  selectedItem: Item | undefined;
  setIsDropdownOpen?: Dispatch<SetStateAction<boolean>>;
  dropdownRef?: RefObject<HTMLDivElement>;
  getPath: (value: string) => string;
  renderSecondaryContent?: () => JSX.Element;
  onDropdownOpen?: () => void;
  onSelect?: (value: string) => void;
  fallbackText?: string;
};

const PageHeading = ({
  items,
  selectedItem,
  renderSecondaryContent,
  getPath,
  onDropdownOpen,
  onSelect,
  fallbackText = 'No Network Selected',
}: PageHeadingProps) => {
  const dropdownRef = useClickOutside<HTMLDivElement>(() => setIsDropdownOpen(false), {
    excludeSelector: '[role="option"]',
  });
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const selectedNetworkNotAmongAuthorisedNetworks = !selectedItem && items?.length === 1;
  const isDropdownEnabled = items?.length > 1 || selectedNetworkNotAmongAuthorisedNetworks;

  const renderHeadingContent = useCallback(
    (showIcon: boolean = false) => (
      <div className="flex flex-row items-center gap-2 truncate w-[92dvw] md:w-[70dvw] lg:w-auto">
        <Heading variant="h1" mb={0}>
          {selectedItem ? selectedItem.label : fallbackText}
        </Heading>
        {showIcon && <Icon size="medium" glyph={ChevronDownRegularGlyph} />}
      </div>
    ),
    [selectedItem, fallbackText]
  );

  const handleDropdownOpen = () => {
    setIsDropdownOpen(true);
    onDropdownOpen?.();
  };

  return (
    <div className="pt-6 pb-10 flex flex-col md:flex-row gap-4 justify-between md:items-center">
      <div className="relative gap-5 max-w-full">
        {isDropdownEnabled ? (
          <Button
            size="large"
            variant="tertiary"
            style={{ padding: '0.25rem 0' }}
            aria-label={'Select Item'}
            onClick={handleDropdownOpen}
            data-testid="page-heading-button"
          >
            {renderHeadingContent(true)}
          </Button>
        ) : (
          renderHeadingContent()
        )}

        {isDropdownOpen && isDropdownEnabled && (
          <Dropdown
            items={items}
            selectedItem={selectedItem}
            setIsDropdownOpen={setIsDropdownOpen}
            dropdownRef={dropdownRef}
            getPath={getPath}
            onSelect={onSelect}
          />
        )}
      </div>

      {renderSecondaryContent && <div className="flex justify-end">{renderSecondaryContent()}</div>}
    </div>
  );
};

const Dropdown = ({
  items,
  selectedItem,
  setIsDropdownOpen,
  dropdownRef,
  getPath,
  onSelect,
}: PageHeadingProps) => {
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const { filters, setFilters } = useFilterContext();

  useEffect(() => {
    // Focus the input when the dropdown is rendered
    inputRef.current?.focus();
  }, []);

  const handleComboboxChange = useCallback(
    (selection: { label: string; value: string } | null): void => {
      if (!selection) return;
      onSelect?.(selection.value);
      navigate(getPath(selection.value));
      setIsDropdownOpen?.(false);

      const newFilters = {
        ...filters,
        selectedShows: [],
      };

      setFilters(newFilters);
    },
    [onSelect, navigate, getPath, setIsDropdownOpen, filters, setFilters]
  );

  return (
    <div className="z-dropdown absolute w-full" ref={dropdownRef}>
      <Combobox
        input={<ComboboxInput ref={inputRef} size="large" />}
        value={selectedItem ?? undefined}
        onChange={(selection) => {
          handleComboboxChange(selection);
        }}
        openOnFocus
        closeOnClear={false}
        openOnClick={false}
      >
        <ComboboxList>
          {items?.map((item) => (
            <ComboboxOption key={item.value} value={item.value} label={item.label} />
          ))}
        </ComboboxList>
      </Combobox>
    </div>
  );
};

export default PageHeading;
